import camelCase from "lodash/camelCase";
import uniqBy from "lodash/uniqBy";
import { ROOT_COLUMN_KEY } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/calendar_content/dual_scrollable_grid/constants";
import { useAvailableWithCustomTypesColumns } from "PFApp/components/table_properties/use_available_with_custom_types_columns";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { TableColumn } from "PFCore/types";
import { useTranslation } from "react-i18next";

const BLOCKED_PROPERTIES = ["duration"];

type UseEngagementsColumnsReturn = {
  availableColumns: TableColumn[];
};

export const useEngagementsColumns = (): UseEngagementsColumnsReturn => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "rowsSidePanel.columns" });
  const engagementTemplate = useTemplate(TemplateKey.Engagement);
  const auditEngagementTemplate = useTemplate(TemplateKey.AuditEngagement);

  // Engagement columns and Audit Engagement columns merged together
  const commonColumns: TableColumn[] = [
    {
      name: ROOT_COLUMN_KEY,
      title: t("engagementTitle"),
      isDefault: true,
      locked: true,
      fixedPosition: true
    },
    {
      name: "status",
      title: t("status")
    },
    {
      name: "postUntil",
      title: t("resourcingCompletedBy"),
      type: "date"
    },
    {
      name: "assignee",
      title: t("assignee")
    },
    {
      name: "due",
      title: t("dueInDays")
    },
    {
      name: "startDate",
      title: t("startDate"),
      type: "date",
      orderKey: "activity.start_date"
    },
    {
      name: "endDate",
      title: t("endDate"),
      type: "date",
      orderKey: "activity.end_date"
    },
    {
      name: "duration",
      title: t("duration"),
      orderKey: "activity.number_of_days"
    },
    {
      name: "createdAt",
      title: t("dateCreated"),
      type: "date",
      orderKey: "activity.saas_created_at"
    },
    {
      name: "principalOwner",
      title: t("principalOwner")
    },
    {
      name: "sourceEngagement",
      title: t("sourceEngagement"),
      type: "rollforward_source"
    },
    {
      name: "targetEngagements",
      title: t("targetEngagements"),
      type: "rollforward_targets"
    }
  ];

  const { customTypesColumns: engagementCustomTypesColumns } = useAvailableWithCustomTypesColumns({
    blockedProperties: BLOCKED_PROPERTIES,
    template: engagementTemplate,
    availableColumns: []
  });

  const { customTypesColumns: auditEngagementCustomTypesColumns } = useAvailableWithCustomTypesColumns({
    blockedProperties: BLOCKED_PROPERTIES,
    template: auditEngagementTemplate,
    availableColumns: []
  });

  const availableColumns = uniqBy(
    [
      ...[...commonColumns, ...engagementCustomTypesColumns, ...auditEngagementCustomTypesColumns].map(
        (column) => ({
          ...column,
          name: camelCase(column.name),
          type: camelCase(column.type) as typeof column.type
        })
      )
    ],
    "name"
  );

  return { availableColumns };
};
